import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Space, message, Divider, Typography, Avatar } from 'antd';
import { UserOutlined, LockOutlined, SaveOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { supabase } from '../lib/supabase';

const { Title, Text } = Typography;

const Profile = ({ user }) => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Kullanıcı profil bilgilerini yükle
  useEffect(() => {
    if (user) {
      setUserProfile({
        full_name: user.user_metadata?.full_name || '',
        username: user.user_metadata?.username || ''
      });
      form.setFieldsValue({
        full_name: user.user_metadata?.full_name || '',
        username: user.user_metadata?.username || ''
      });
      setProfileLoading(false);
    }
  }, [user, form]);

  // Profil bilgilerini güncelle
  const handleProfileUpdate = async (values) => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.updateUser({
        data: {
          full_name: values.full_name,
          username: values.username
        }
      });

      if (error) {
        throw error;
      }

      message.success('Profil bilgileri başarıyla güncellendi');
      setUserProfile({
        full_name: values.full_name,
        username: values.username
      });
    } catch (error) {
      message.error('Profil güncellenirken hata oluştu: ' + error.message);
      console.error('Profile update error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Şifre değiştir
  const handlePasswordChange = async (values) => {
    try {
      setPasswordLoading(true);
      
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword
      });

      if (error) {
        throw error;
      }

      message.success('Şifre başarıyla değiştirildi');
      passwordForm.resetFields();
    } catch (error) {
      message.error('Şifre değiştirilirken hata oluştu: ' + error.message);
      console.error('Password change error:', error);
    } finally {
      setPasswordLoading(false);
    }
  };

  const getInitials = (email) => {
    return email ? email.substring(0, 2).toUpperCase() : 'U';
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        
        {/* Kullanıcı Bilgileri Kartı */}
        <Card
          title={
            <Space>
              <UserOutlined />
              User Profile
            </Space>
          }
          loading={profileLoading}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            
            {/* Avatar ve Temel Bilgiler */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Avatar 
                size={80} 
                style={{ 
                  backgroundColor: '#1890ff',
                  fontSize: '24px',
                  marginBottom: '16px'
                }}
              >
                {getInitials(user?.email)}
              </Avatar>
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {userProfile?.full_name || 'Kullanıcı'}
                </Title>
                <Text type="secondary">{user?.email}</Text>
              </div>
            </div>

            {/* Profil Düzenleme Formu */}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleProfileUpdate}
              style={{ maxWidth: '500px', margin: '0 auto', width: '100%' }}
            >
              <Form.Item
                label="Full Name"
                name="full_name"
                rules={[
                  { required: true, message: 'Ad soyad gereklidir' },
                  { min: 2, message: 'Ad soyad en az 2 karakter olmalıdır' }
                ]}
              >
                <Input 
                  placeholder="Ad soyadınızı girin"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Username"
                name="username"
                rules={[
                  { required: true, message: 'Kullanıcı adı gereklidir' },
                  { min: 3, message: 'Kullanıcı adı en az 3 karakter olmalıdır' }
                ]}
              >
                <Input 
                  placeholder="Kullanıcı adınızı girin"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  style={{ width: '100%' }}
                >
                  Update Profile
                </Button>
              </Form.Item>
            </Form>
          </Space>
        </Card>

        {/* Şifre Değiştirme Kartı */}
        <Card
          title={
            <Space>
              <LockOutlined />
              Change Password
            </Space>
          }
        >
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handlePasswordChange}
            style={{ maxWidth: '500px', margin: '0 auto' }}
          >
            <Form.Item
              label="New Password"
              name="newPassword"
              rules={[
                { required: true, message: 'New password is required' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
            >
              <Input.Password 
                placeholder="Enter new password"
                size="large"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <Form.Item
              label="Confirm New Password"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Password confirmation is required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password 
                placeholder="Enter new password confirmation"
                size="large"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={passwordLoading}
                icon={<LockOutlined />}
                size="large"
                style={{ width: '100%' }}
                danger
              >
                Change Password
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* Account Information */}
        <Card title="Account Information">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong>Email:</Text>
              <Text>{user?.email}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong>Account Creation:</Text>
              <Text>{user?.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : 'Unknown'}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong>Last Login:</Text>
              <Text>{user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString('tr-TR') : 'Bilinmiyor'}</Text>
            </div>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default Profile;